import type { FunctionComponentWithRef } from "react";
import React from "react";
import { routes } from "scmp-app/lib/router";
import { getIsSubSection, shouldRedirectBaseDynamicSectionLink } from "scmp-app/lib/router/helpers";
import { RouteType, useRouteDetector } from "scmp-app/lib/router/hooks";

import type { Props as BaseLinkProps } from "shared/components/common/base-link";
import { BaseLink } from "shared/components/common/base-link";
import { getAbsoluteUrl } from "shared/lib/utils";

import type { LinkEntity } from "./types";

export type Props = Omit<BaseLinkProps, "as" | "href" | "pathname"> & {
  className?: string;
  linkEntity: LinkEntity;
};

export const BaseEntityLink: FunctionComponentWithRef<Props, HTMLAnchorElement> = ({
  ref: forwardReference,
  ...props
}) => {
  const { children, hash, linkEntity, query: queryProps, target, ...remainingProps } = props;

  const renderLink = ({
    hash,
    isAnchor,
    linkAs,
    pathname,
    query,
    target = "_self",
  }: {
    hash?: BaseLinkProps["hash"];
    isAnchor?: boolean;
    linkAs?: BaseLinkProps["as"];
    pathname: string;
    query?: BaseLinkProps["query"];
    target?: BaseLinkProps["target"];
  }) => (
    <BaseLink
      {...remainingProps}
      anchorProps={{ target, ...props.anchorProps }}
      as={linkAs}
      hash={hash}
      isAnchor={isAnchor}
      pathname={pathname}
      query={{ ...query, ...queryProps }}
      ref={forwardReference}
      target={target}
    >
      {children}
    </BaseLink>
  );

  const {
    entityId,
    parentSection,
    routeType: routeTypeInEntity = "",
    urlAlias,
    urlRedirectPath,
  } = linkEntity;
  const defaultRenderLink = renderLink({ pathname: urlAlias, target });
  const renderMap = {
    [RouteType.DrupalRedirect]: () => renderLink({ pathname: getAbsoluteUrl(urlAlias), target }),
    [RouteType.Dynamic]: () => {
      const typeName =
        routeTypeInEntity === "Section" &&
        getIsSubSection({
          entityId,
          parentSection,
        })
          ? "SubSection"
          : routeTypeInEntity;

      const matched = routes.dynamics[typeName];
      const { pathname, query } = matched.route(entityId);

      return renderLink({
        hash,
        isAnchor: shouldRedirectBaseDynamicSectionLink(pathname, queryProps),
        linkAs: urlAlias,
        pathname,
        query,
        target,
      });
    },
    [RouteType.ExternalDomain]: () => renderLink({ pathname: urlAlias, target: "_blank" }),
    [RouteType.InternalDomain]: () => defaultRenderLink,
    [RouteType.Other]: () => defaultRenderLink,
    [RouteType.Redirect]: () => renderLink({ pathname: getAbsoluteUrl(urlRedirectPath), target }),
    [RouteType.Static]: () => defaultRenderLink,
  };
  const { getRouteTypeByRouteEntity } = useRouteDetector();
  return renderMap[getRouteTypeByRouteEntity(linkEntity)]?.();
};

BaseEntityLink.displayName = "BaseEntityLink";
